import React from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { makeStyles } from "@material-ui/core/styles";
import FarApi from "../../../../assets/js/utils/far/farApi";
import farPageStyle from "../../../../assets/jss/user/far/farPageStyle.jsx";
import FarStatusBanner from "./FarStatusBanner";
import FarHelpPanel from "./FarHelpPanel";
import FarRulesTab from "./FarRulesTab";
import FarSettingsTab from "./FarSettingsTab";
import FarAuditLogTab from "./FarAuditLogTab";

const useStyles = makeStyles(theme => farPageStyle(theme));

const TABS = [
    { id: "rules", label: "Rules" },
    { id: "settings", label: "Settings" },
    { id: "audit", label: "Audit Log" }
];

const SUBTITLES = {
    rules: "Validate recipients based on file name, file location and defined rules when sending files through the portal.",
    settings: "Configure how File Attachment Rules work for your organization.",
    audit: "Review rule matches, exceptions and the actions senders took."
};

function FarManagementPage({ enabled, canManage, initialTab, onTabChange }){
    const classes = useStyles();
    const [tab, setTab] = React.useState(TABS.some(t => t.id === initialTab) ? initialTab : "rules");
    const [rules, setRules] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState(null);
    const sourceRef = React.useRef(null);

    const loadRules = React.useCallback((showLoader = true) => {
        if(sourceRef.current){ sourceRef.current.cancel("Request Cancelled"); }
        const source = axios.CancelToken.source();
        sourceRef.current = source;
        if(showLoader){ setLoading(true); }
        setLoadError(null);
        FarApi.getRules({}, source).then(list => {
            setRules(list);
            setLoading(false);
        }).catch(err => {
            if(err && err.message === "Request Cancelled"){ return; }
            setLoading(false);
            setLoadError((err && err.message) || "Rules could not be loaded.");
        });
    }, []);

    React.useEffect(() => {
        loadRules();
        return () => { if(sourceRef.current){ sourceRef.current.cancel("Request Cancelled"); } };
    }, [loadRules]);

    const selectTab = (id) => {
        setTab(id);
        if(onTabChange){ onTabChange(id); }
    };

    const onTabKeyDown = (e, index) => {
        let next = null;
        if(e.key === "ArrowRight"){ next = (index + 1) % TABS.length; }
        if(e.key === "ArrowLeft"){ next = (index - 1 + TABS.length) % TABS.length; }
        if(next !== null){
            e.preventDefault();
            selectTab(TABS[next].id);
            const el = document.getElementById("far-tab-"+TABS[next].id);
            if(el){ el.focus(); }
        }
    };

    return (
        <div className={classes.page}>
            <div className={classes.header}>
                <div className={classes.headerText}>
                    <h1 className={classes.title}>File Attachment Rules (FAR)</h1>
                    <p className={classes.subtitle}>{SUBTITLES[tab]}</p>
                </div>
                <div className={classes.headerBanner}>
                    <FarStatusBanner enabled={enabled} canManage={canManage} />
                </div>
            </div>
            <div className={classes.tabs} role="tablist" aria-label="File Attachment Rules sections">
                {
                    TABS.map((item, index) => (
                        <button type="button" key={item.id} id={"far-tab-"+item.id} role="tab"
                            aria-selected={tab === item.id ? "true" : "false"} aria-controls={"far-panel-"+item.id}
                            tabIndex={tab === item.id ? 0 : -1}
                            className={classes.tab+" "+(tab === item.id ? classes.tabActive : "")}
                            onClick={() => selectTab(item.id)} onKeyDown={(e) => onTabKeyDown(e, index)}>
                            {item.label}
                        </button>
                    ))
                }
            </div>
            <div className={classes.layout}>
                <div role="tabpanel" id={"far-panel-"+tab} aria-labelledby={"far-tab-"+tab} style={{minWidth: 0}}>
                    {
                        tab === "rules" ?
                            <FarRulesTab rules={rules} loading={loading} loadError={loadError} canManage={canManage}
                                onRulesChanged={() => loadRules(false)} onRetry={() => loadRules(true)} />
                        : tab === "settings" ?
                            <FarSettingsTab canManage={canManage} />
                        :
                            <FarAuditLogTab />
                    }
                </div>
                <FarHelpPanel variant={tab === "rules" ? "rules" : "settings"} />
            </div>
        </div>
    );
}

FarManagementPage.defaultProps = { enabled: true, canManage: true, initialTab: "rules", onTabChange: null };
FarManagementPage.propTypes = {
    enabled: PropTypes.bool,
    canManage: PropTypes.bool,
    initialTab: PropTypes.string,
    onTabChange: PropTypes.func
};

export default FarManagementPage;
